import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageIcon } from '@/components/icons';
import {
  useFilterEvents,
  useDeleteEvent,
  useUpdateEventStatus,
  usePublishedEventsCount,
  useDraftEventsCount,
  useSearchEvents
} from '@/lib/api/hooks/event';
import type { Event as EventType } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { CalendarDays, Eye, MapPin, Pencil } from 'lucide-react';
import { Tab, Button, ActionDropdown, TanStackTable, DeleteModal, PublishModal, UnpublishModal, type TabItem, type DropdownAction, type ColumnDef, Pagination } from '@/components/ui';
import EventsStats from './EventsStats';
import { Paragraph } from '@/components/ui/Typography';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import FilterModule from '@/components/ui/FilterModule';
import Search from '@/components/ui/Search';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { useEventsFilterSections } from '@/pages/events/FilterModule/FILTER_SECTIONS';

type EventTableRow = EventType & {
  updatedBy?: string | null;
  updatedByName?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
};

const Events: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedEventForDelete, setSelectedEventForDelete] = useState<EventType | null>(null);
  const [selectedEventForStatusUpdate, setSelectedEventForStatusUpdate] = useState<EventType | null>(null);

   const FILTER_SECTIONS = useEventsFilterSections();
              // ── URL-driven search / filter state ──────────────────────────────────────
              const {
                mode,
                searchTerm,
                filterState,
                page,
                size,
                setSearch,
                setFilters,
                removeFilter,
                clearFilters,
                setPage,
                apiFilters,
                getActiveFilterCount
              } = useSearchFilterParams(FILTER_SECTIONS)
            
          
          
            // Search API (when searching)
            const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchEvents(
            searchTerm,
            mode === 'search'
            )
          
            // Filter API (when filtering or default)
            const { data: filterData, isFetching: isFilterFetching, isError: isFilterError, refetch: refetchFilter } = useFilterEvents(
              { page, size },
              {   status: activeTab === 'published' ? 'PUBLISH' : 'DRAFT', ...apiFilters }
            )
          
            // Select appropriate data source: search takes priority
            const aggregateData = mode === 'search' ? searchData : filterData
            const isFetching = mode === 'search' ? isSearchFetching : isFilterFetching
            const isError = mode === 'search' ? isSearchError : isFilterError
          
            const totalPages = aggregateData?.data?.totalPage ?? 1
            const activeFilterCount = getActiveFilterCount()
            const refetchData = mode === 'search' ? refetchSearch : refetchFilter
            const emptyMessage = mode === 'search'
              ? 'No events found matching your search criteria.'
              : mode === "filter" ?  'No events found matching your filter criteria.'
              : 'No events found.'
    
      const events = aggregateData?.data?.data || [];

  const { data: publishedCountData } = usePublishedEventsCount();
  const { data: draftCountData } = useDraftEventsCount();


  const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvent();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateEventStatus();

  const publishedCount = publishedCountData?.data || 0;
  const draftsCount = draftCountData?.data || 0;

  const tabItems: TabItem<'published' | 'drafts'>[] = [
    { key: 'published', label: 'Published', count: publishedCount },
    { key: 'drafts', label: 'Drafts', count: draftsCount }
  ];

  const handleEdit = (eventId: string) => {
    navigate(`/admin/events/edit/${eventId}`);
  };

  const handleViewDetails = (eventId: string) => {
    navigate(`/admin/events/${eventId}`);
  };

  const handleDeleteEvent = (event: EventType) => {
    setSelectedEventForDelete(event);
    setDeleteModalOpen(true);
  };

  const handlePublishEvent = (event: EventType) => {
    setSelectedEventForStatusUpdate(event);
    setPublishModalOpen(true);
  };

  const handleUnpublishEvent = (event: EventType) => {
    setSelectedEventForStatusUpdate(event);
    setUnpublishModalOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedEventForDelete) return;

    deleteEvent(selectedEventForDelete.eventId, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Event deleted successfully!');
          setDeleteModalOpen(false);
          setSelectedEventForDelete(null);
        }
      },
      onError: (error: Error) => {
        showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
      }
    });
  };

  const confirmStatusUpdate = () => {
    if (!selectedEventForStatusUpdate) return;

    const newStatus = selectedEventForStatusUpdate.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateStatus(
      { eventId: selectedEventForStatusUpdate.eventId, data: { status: newStatus } },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success(
              newStatus === 'PUBLISH'
                ? 'Event published successfully!'
                : 'Event unpublished successfully!'
            );
            setPublishModalOpen(false);
            setUnpublishModalOpen(false);
            setSelectedEventForStatusUpdate(null);
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const getDropdownActions = (event: EventType): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: 'View Details',
        onClick: (e) => {
          e.stopPropagation();
          handleViewDetails(event.eventId);
        }
      },
      {
        label: 'Edit',
        onClick: (e) => {
          e.stopPropagation();
          handleEdit(event.eventId);
        }
      },
      {
        label: event.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
        onClick: (e) => {
          e.stopPropagation();
          if (event.status === 'PUBLISH') {
            handleUnpublishEvent(event);
          } else {
            handlePublishEvent(event);
          }
        },
        disabled: isUpdatingStatus
      }
    ];

    // Only show Delete for draft events
    if (event.status === 'DRAFT') {
      actions.push({
        label: 'Delete',
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteEvent(event);
        },
        variant: 'danger' as const,
        disabled: isDeleting
      });
    }

    return actions;
  };

  const formatCompactDate = (value: string | null | undefined) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  const formatLongDate = (value: string | null | undefined) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateRange = (startDate: string | null | undefined, endDate: string | null | undefined) => {
    if (!startDate && !endDate) return 'N/A';
    if (startDate && endDate) {
      return `${formatLongDate(startDate)} - ${formatLongDate(endDate)}`;
    }
    return formatLongDate(startDate || endDate || '');
  };

  const getEventStatus = (event: EventTableRow) => {
    const now = new Date();
    const startDate = event.startDate ? new Date(event.startDate) : null;
    const endDate = event.endDate ? new Date(event.endDate) : null;

    if (event.status === 'DRAFT') {
      return {
        label: 'Draft',
        className: 'bg-blue-50 text-blue-700 border-blue-200',
        dotClassName: 'bg-blue-600'
      };
    }

    if (endDate && endDate < now) {
      return {
        label: 'Past',
        className: 'bg-gray-100 text-gray-700 border-gray-200',
        dotClassName: 'bg-gray-500'
      };
    }

    if (startDate && startDate <= now && endDate && endDate >= now) {
      return {
        label: 'Published',
        className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        dotClassName: 'bg-emerald-600'
      };
    }

    return {
      label: 'Upcoming',
      className: 'bg-sky-50 text-sky-700 border-sky-200',
      dotClassName: 'bg-sky-600'
    };
  };

  const columns: ColumnDef<EventTableRow, unknown>[] = [
    {
      id: 'event',
      header: 'EVENT',
      cell: ({ row }) => {
        const event = row.original;
        const eventStatus = getEventStatus(event);
        const monthLabel = event.startDate ? new Date(event.startDate).toLocaleDateString('en-GB', { month: 'short' }).toUpperCase() : 'N/A';
        const dayLabel = event.startDate ? new Date(event.startDate).getDate().toString().padStart(2, '0') : '--';
        const weekdayLabel = event.startDate ? new Date(event.startDate).toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase() : '--';

        return (
          <div className="flex items-start gap-4 max-w-[600px]">
            {event.imageUrl ? (
              <img src={event.imageUrl} alt={event.eventTitle} className="h-24 w-32 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="h-24 w-32 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <ImageIcon className="h-8 w-8 text-gray-400" />
              </div>
            )}

            <div className="grid grid-cols-[auto_1fr] gap-3 w-full">
              <div className="flex w-16 flex-col items-center rounded-xl border border-gray-200 bg-white py-2 text-center shadow-sm">
                <p className="text-sm font-semibold text-[#8F2F44]">{monthLabel}</p>
                <p className="text-3xl font-semibold leading-none text-gray-900 mt-1">{dayLabel}</p>
                <p className="mt-1 text-xs font-semibold text-gray-500">{weekdayLabel}</p>
              </div>

              <div className="pt-1 overflow-hidden w-full">
                <p className="truncate text-base font-semibold text-gray-900">{event.eventTitle}</p>
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="truncate">{event.eventVenue}</span>
                </div>
                <span className={`mt-3 inline-flex rounded-md px-2.5 py-1 text-xs font-semibold ${eventStatus.className}`}>
                  {eventStatus.label}
                </span>
              </div>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'dateVenue',
      header: 'DATE & VENUE',
      cell: ({ row }) => {
        const event = row.original;
        return (
          <div className="min-w-[260px] space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <CalendarDays className="h-4 w-4 shrink-0 text-gray-400" />
              <span>{formatDateRange(event.startDate, event.endDate)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="truncate">{event.eventVenue}</span>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'publishedUpdated',
      header: 'PUBLISHED / UPDATED',
      cell: ({ row }) => {
        const event = row.original;
        const updatedByName = event.updatedByName || event.updatedBy || 'N/A';
        return (
          <div className="min-w-[220px] space-y-2 text-sm text-gray-700">
            <div>
              <p className="text-gray-500">Published: {event.datePublished ? formatCompactDate(event.datePublished) : 'Not published'}</p>
              <p className="font-medium text-gray-900">By: {event.publishedByName || event.createdByName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-gray-500">Updated: {event.updatedAt ? formatCompactDate(event.updatedAt) : 'N/A'}</p>
              <p className="font-medium text-gray-900">By: {updatedByName}</p>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'status',
      header: 'STATUS',
      cell: ({ row }) => {
        const status = getEventStatus(row.original);
        return (
          <span className={`inline-flex items-center gap-2 rounded-md border px-2 py-1 text-sm font-semibold ${status.className}`}>
            <span className={`h-2 w-2 rounded-full ${status.dotClassName}`} />
            {status.label}
          </span>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600 text-center', cellClassName: 'px-4 py-4 text-center' },
    },
    {
      id: 'actions',
      header: 'ACTIONS',
      cell: ({ row }) => {
        const event = row.original;
        return (
          <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              title="View"
              onClick={() => handleViewDetails(event.eventId)}
              className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-gray-200 text-emerald-700 hover:bg-emerald-50"
            >
              <Eye className="w-5 h-5" />
            </button>
            <button
              type="button"
              title="Edit"
              onClick={() => handleEdit(event.eventId)}
              className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-gray-200 text-emerald-700 hover:bg-emerald-50"
            >
              <Pencil className="w-5 h-5" />
            </button>
            <ActionDropdown id={event.eventId} actions={getDropdownActions(event)} />
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case text-gray-600 text-center', cellClassName: 'px-4 py-4 text-center' },
    },
  ];

  const isEmptyState = !isFetching && mode === "default" && events.length === 0;


  return (
    <>
      <EventsStats />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Tab
            tabs={tabItems}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <Button
            variant="primary"
            onClick={() => navigate('/admin/events/add')}
          >
            Post Event
          </Button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                {/* Search + Filter Controls */}
                  <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
                    <div className="flex items-center relative gap-4 justify-between flex-wrap">
                      <Search
                        value={searchTerm}
                        onSearch={setSearch}
                        placeholder="Search events"
                      />
                       <FilterModule
                            sections={FILTER_SECTIONS}
                            activeFilters={filterState}
                            onApply={setFilters}
                            activeFilterCount={activeFilterCount}
                          />
                    </div>
        
                    {/* Active filter chips — only shown in filter mode */}
                    <FilterParamsDisplay
                      sections={FILTER_SECTIONS}
                      filterState={filterState}
                      onRemove={removeFilter}
                      onClearAll={clearFilters}
                    />
                  </div>

          {isFetching ? <TanStackTable data={[]} columns={columns} loading /> :
              isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading data.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        )  : isEmptyState  ? (
                <div className="text-center py-20">
                  <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                    <img src="/No-event-icon.png" alt="No events" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg">
                    {activeTab === 'published' ? 'No Recent Event' : 'No Draft'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {activeTab === 'published' ? 'You are yet to create an event' : 'Your draft is empty'}
                  </p>
                    <Button variant="primary" className="mt-4" onClick={() => navigate('/admin/events/add')}>
                      Post Event
                    </Button>
                </div>
              ) : (
            <>
            <div className="overflow-x-auto">
              <TanStackTable<EventTableRow>
                data={events}
                columns={columns}
                getRowId={(row) => row.eventId}
                onRowClick={(row) => handleViewDetails(row.eventId)}
                tableClassName="min-w-[1600px] w-full divide-y divide-gray-200"
                theadClassName="bg-[#FAFAFA]"
                tbodyClassName="bg-white divide-y divide-gray-200"
                rowClassName="hover:bg-gray-50 transition-colors"
                    emptyMessage={emptyMessage}
                emptyClassName="px-6 py-12 text-center text-sm text-gray-500"
              />
            </div>
                               <Pagination   currentPage={page}
                          totalPages={totalPages}
                          onPageChange={setPage}
                        className="p-4"/>
              </>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Event"
        subtitle="Are you sure you want to delete this event?"
        itemName={selectedEventForDelete?.eventTitle}
        warningText="This action cannot be undone. The event will be permanently deleted."
        isLoading={isDeleting}
      />

      {/* Publish Modal */}
      {selectedEventForStatusUpdate && selectedEventForStatusUpdate.status === 'DRAFT' && (
        <PublishModal
          isOpen={publishModalOpen}
          onClose={() => setPublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish Event?"
          subtitle="Are you sure you want to publish this event?"
          itemName={selectedEventForStatusUpdate.eventTitle}
          infoText="This event will be visible to the public once published."
          itemType="Event"
          isLoading={isUpdatingStatus}
        />
      )}

      {/* Unpublish Modal */}
      {selectedEventForStatusUpdate && selectedEventForStatusUpdate.status === 'PUBLISH' && (
        <UnpublishModal
          isOpen={unpublishModalOpen}
          onClose={() => setUnpublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="Event"
          isLoading={isUpdatingStatus}
        />
      )}
    </>
  );
};

export default Events;

